import { useState } from "react";
import { Check, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { useToasts } from "./Toasts";

/** Real Approve / Reject controls for a pending approval.
 * Director-only. Non-directors see disabled controls with a tooltip.
 */
export default function ApprovalActions({ approval, onDecided, size = "small" }) {
  const { user } = useAuth();
  const push = useToasts((s) => s.push);
  const isDirector = user?.role === "director";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);

  if (approval.decision !== "pending") {
    return (
      <span className={`pill ${approval.decision === "approved" ? "green" : "red"}`}>
        {approval.decision}
      </span>
    );
  }

  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/approvals/${approval.id}/decide`, {
        decision,
        reason: reason.trim() || undefined,
      });
      push(`Approval ${decision}`, decision === "approved" ? "ok" : "err");
      setShowRejectForm(false);
      setReason("");
      onDecided?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      push(typeof detail === "string" ? detail : "Decision failed", "err");
    } finally {
      setBusy(false);
    }
  };

  if (!isDirector) {
    return (
      <div className="row-actions">
        <button className="btn small approve-btn" disabled title="Only Directors can approve">
          <Check size={12} /> Approve
        </button>
        <button className="btn small reject-btn" disabled title="Only Directors can reject">
          <X size={12} /> Reject
        </button>
      </div>
    );
  }

  if (showRejectForm) {
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required to reject)"
          data-testid={`approval-${approval.id}-reason`}
          style={{ minWidth: 200 }}
        />
        <button
          className={`btn ${size} reject-btn`}
          data-testid={`approval-${approval.id}-confirm-reject`}
          disabled={busy || !reason.trim()}
          onClick={() => decide("rejected")}
        >
          {busy ? <span className="loader" /> : "Confirm reject"}
        </button>
        <button className={`btn ${size}`} onClick={() => setShowRejectForm(false)}>Cancel</button>
      </div>
    );
  }

  return (
    <div className="row-actions">
      <button
        className={`btn ${size} approve-btn`}
        data-testid={`approval-${approval.id}-approve`}
        disabled={busy}
        onClick={() => decide("approved")}
      >
        {busy ? <span className="loader" /> : <><Check size={12} /> Approve</>}
      </button>
      <button
        className={`btn ${size} reject-btn`}
        data-testid={`approval-${approval.id}-reject`}
        disabled={busy}
        onClick={() => setShowRejectForm(true)}
      >
        <X size={12} /> Reject
      </button>
    </div>
  );
}
